import { ErrorBoundary } from 'react-error-boundary';

import union from 'lodash/union';

// Hooks
import useUser from '@/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

// Components
import InfoBanner from '@/components/base/InfoBanner';
import LoadMoreButton from '@/components/composite/Button/LoadMoreButton';
import { Illustration } from '@/components/composite/Illustration';
import ErrorDefaultFallback from '@/components/layouts/ErrorFallbacks/ErrorDefaultFallback';
import CollaborationNotificationItem from '@/components/pages/scheduler/collaboration/notifications/CollaborationNotificationItem';

//image
import illustrationEn from './../../../../../../public/images/illustrations/illustrations-empty-en.webp';
import illustrationEs from './../../../../../../public/images/illustrations/illustrations-empty-es.webp';
import illustrationFr from './../../../../../../public/images/illustrations/illustrations-empty-fr.webp';

const CollaborationNotificationsFeed = ({
    notifications,
    notificationsError,
    isLoadMore,
    isLoadingMore,
    isLoadingInitialData,
    mutateNotifications,
    loadMoreNotifications,
    checkedIds,
    setCheckedIds,
    openFunnel
}) => {
    const { t } = useTranslation();

    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    if (isLoadingInitialData) {
        return (
            <main
                className="flex w-full animate-pulse flex-col space-y-3"
                data-testid="loading-data">
                {Array.apply(null, { length: 5 }).map((e, index) => (
                    <span
                        key={index}
                        className="block h-full min-h-[7.125rem] w-full rounded-md bg-icoGray-300 dark:bg-icoDarkMode-deepBlack"
                    />
                ))}
            </main>
        );
    }

    if (notificationsError) {
        return (
            <main className="flex flex-1 items-start justify-center text-icoGray-500 dark:text-icoDarkMode-wolf">
                <InfoBanner
                    className="mt-12"
                    title={t('errors:error_occured')}
                    message={t('errors:error_occured_fetch_data')}
                />
            </main>
        );
    }

    // No data
    if (notifications?.[0]?.data?.length === 0) {
        return (
            <main className="flex flex-1 flex-col items-center justify-center space-y-2 text-icoGray-500 dark:text-icoDarkMode-wolf">
                <Illustration
                    src={{ en: illustrationEn, fr: illustrationFr, es: illustrationEs }}
                />

                <span> {t('scheduler:collaboration.notifications.no_notifications')}</span>
            </main>
        );
    }

    const allNotificationsIds = notifications.reduce((previousPage, currentPage) => {
        return [
            ...previousPage,
            ...currentPage.data.reduce((current, previous) => {
                return [...current, ...[previous.id]];
            }, [])
        ];
    }, []);

    return (
        <ErrorBoundary
            FallbackComponent={ErrorDefaultFallback}
            onReset={() => mutateNotifications(null)}>
            <div className="mb-4 flex items-center justify-end gap-3 text-xs">
                {allNotificationsIds.filter((id) => !checkedIds.includes(id)).length ? (
                    <button
                        onClick={() =>
                            setCheckedIds((prevState) => union(prevState, allNotificationsIds))
                        }
                        className="text-icoBlue">
                        {t(
                            'scheduler:collaboration.notifications.select_all_visible_notifications'
                        )}
                    </button>
                ) : null}
                {checkedIds.length ? (
                    <button
                        onClick={() => setCheckedIds([])}
                        className="text-icoGray-500 dark:text-icoDarkMode-wolf">
                        {t('scheduler:collaboration.notifications.unselect_all_notifications')}
                    </button>
                ) : null}
            </div>
            <main className="flex w-full flex-col space-y-3">
                {notifications?.map((items) => {
                    return items?.data?.map((notification) => (
                        <CollaborationNotificationItem
                            key={notification?.id}
                            user={user}
                            notification={notification}
                            isChecked={checkedIds.includes(notification.id)}
                            mutateNotifications={mutateNotifications}
                            setCheckedIds={setCheckedIds}
                            openFunnel={openFunnel}
                        />
                    ));
                })}
            </main>
            {isLoadMore ? (
                <div className="mt-6 flex justify-center">
                    <LoadMoreButton
                        isLoadingMore={isLoadingMore}
                        loadMoreItems={loadMoreNotifications}
                    />
                </div>
            ) : null}
        </ErrorBoundary>
    );
};

CollaborationNotificationsFeed.propTypes = {
    notifications: PropTypes.array,
    notificationsError: PropTypes.object,
    isLoadMore: PropTypes.bool,
    isLoadingMore: PropTypes.bool,
    isLoadingInitialData: PropTypes.bool,
    mutateNotifications: PropTypes.func,
    loadMoreNotifications: PropTypes.func,
    checkedIds: PropTypes.array,
    setCheckedIds: PropTypes.func,
    openFunnel: PropTypes.func
};

export default CollaborationNotificationsFeed;
